import React, { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Plus, 
  Filter,
  Search,
  Grid3x3,
  List,
  Clock,
  CheckCircle,
  AlertCircle,
  MoreHorizontal
} from 'lucide-react'
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  isSameMonth,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  addDays
} from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu'
import { SocialPost, ContentCalendarEvent, Platform } from '@/types/social'
import PostCreateDialog from '@/features/social/components/PostCreateDialog'

// Mock data for the calendar
const mockCalendarEvents: ContentCalendarEvent[] = [
  {
    id: '1',
    title: 'Beautiful brick restoration project',
    date: '2025-01-15',
    type: 'post',
    platform: 'INSTAGRAM',
    status: 'SCHEDULED',
    post_id: '1',
    description: 'Downtown Toronto heritage building restoration',
    color: 'bg-purple-500'
  },
  {
    id: '2',
    title: 'Winter Chimney Safety Campaign',
    date: '2025-01-16',
    type: 'campaign',
    description: 'Multi-platform safety awareness campaign',
    color: 'bg-blue-500'
  },
  {
    id: '3',
    title: 'New Year Resolution: Home Maintenance',
    date: '2025-01-20',
    type: 'post',
    platform: 'FACEBOOK',
    status: 'DRAFT',
    description: 'Educational content about annual home maintenance',
    color: 'bg-green-500'
  },
  {
    id: '4',
    title: 'Family Day Holiday',
    date: '2025-02-17',
    type: 'holiday',
    description: 'Canadian statutory holiday - adjust posting schedule',
    color: 'bg-orange-500'
  }
]

const mockPosts: SocialPost[] = [
  {
    id: '1',
    content: 'Beautiful brick restoration project completed in Downtown Toronto! 🧱✨',
    caption: 'Our expert team transformed this heritage building with precision tuckpointing and brick replacement.',
    image_urls: ['https://via.placeholder.com/400x300'],
    media: null,
    status: 'SCHEDULED',
    workflow_stage: 'APPROVED',
    scheduled_time: '2025-01-15T15:00:00Z',
    published_at: null,
    created_by: 'user1',
    updated_by: null,
    profile_id: '1',
    queue_id: null,
    queue_position: null,
    platform_specific_data: { platform: 'INSTAGRAM' },
    is_template: false,
    tags: ['project', 'restoration'],
    hashtags: ['#MasonryRepair', '#TorontoConstruction'],
    mentions: null,
    time_slot: '15:00',
    engagement_metrics: null,
    campaign_data: null,
    approval_required: true,
    approved_by: 'manager1',
    approved_at: '2025-01-14T10:00:00Z',
    ai_generated: true,
    created_at: '2025-01-14T09:00:00Z',
    updated_at: '2025-01-14T10:00:00Z'
  }
]

const PLATFORM_COLORS: Record<Platform, string> = {
  INSTAGRAM: 'bg-gradient-to-r from-purple-500 to-pink-500',
  FACEBOOK: 'bg-blue-600',
  TWITTER: 'bg-sky-400',
  LINKEDIN: 'bg-blue-700',
  PINTEREST: 'bg-red-600',
  TIKTOK: 'bg-black'
}

export default function ContentCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'month' | 'week' | 'list'>('month')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  // Mock data queries
  const { data: events = mockCalendarEvents } = useQuery({
    queryKey: ['calendar-events', format(currentDate, 'yyyy-MM')],
    queryFn: () => Promise.resolve(mockCalendarEvents)
  })

  const { data: posts = mockPosts } = useQuery({
    queryKey: ['scheduled-posts', format(currentDate, 'yyyy-MM')],
    queryFn: () => Promise.resolve(mockPosts)
  })

  // Calendar calculations
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calendarStart = startOfWeek(monthStart)
  const calendarEnd = endOfWeek(monthEnd)

  const calendarDays = eachDayOfInterval({
    start: calendarStart,
    end: calendarEnd
  })

  const weekDays = useMemo(() => {
    if (viewMode !== 'week') return []
    const weekStart = startOfWeek(selectedDate || currentDate)
    return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  }, [selectedDate, currentDate, viewMode])

  // Filter events and posts
  const filteredEvents = useMemo(() => {
    let filtered = events

    if (selectedPlatform !== 'all') {
      filtered = filtered.filter(event => event.platform === selectedPlatform)
    }

    if (searchQuery) {
      filtered = filtered.filter(event =>
        event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        event.description?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    return filtered
  }, [events, selectedPlatform, searchQuery])

  const getEventsForDate = (date: Date) => {
    const dateStr = format(date, 'yyyy-MM-dd')
    return filteredEvents.filter(event => event.date === dateStr)
  }

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => 
      direction === 'prev' ? subMonths(prev, 1) : addMonths(prev, 1)
    )
  }

  const handleDateClick = (date: Date) => {
    setSelectedDate(date)
  }

  const getEventTypeIcon = (type: ContentCalendarEvent['type']) => {
    switch (type) {
      case 'post':
        return <Calendar className="h-3 w-3" />
      case 'campaign':
        return <Grid3x3 className="h-3 w-3" />
      case 'holiday':
        return <AlertCircle className="h-3 w-3" />
      case 'event':
        return <Clock className="h-3 w-3" />
      default:
        return <Calendar className="h-3 w-3" />
    }
  }

  const renderCalendarDay = (date: Date) => {
    const dayEvents = getEventsForDate(date)
    const isToday = isSameDay(date, new Date())
    const isCurrentMonth = isSameMonth(date, currentDate)
    const isSelected = selectedDate && isSameDay(date, selectedDate)

    return (
      <div
        key={date.toISOString()}
        className={`
          min-h-[120px] p-2 border-r border-b cursor-pointer transition-colors
          ${!isCurrentMonth ? 'bg-muted/30 text-muted-foreground' : 'hover:bg-accent/50'}
          ${isSelected ? 'bg-primary/10 border-primary' : ''}
        `}
        onClick={() => handleDateClick(date)}
      >
        <div className="flex items-center justify-between mb-2">
          <span className={`
            text-sm font-medium
            ${isToday ? 'bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center' : ''}
          `}>
            {format(date, 'd')}
          </span>
          {dayEvents.length > 0 && (
            <Badge variant="secondary" className="text-xs">
              {dayEvents.length}
            </Badge>
          )}
        </div>
        
        <div className="space-y-1">
          {dayEvents.slice(0, 3).map(event => (
            <div
              key={event.id}
              className={`
                text-xs p-1 rounded text-white truncate
                ${event.color || 'bg-gray-500'}
              `}
              title={event.title}
            >
              <div className="flex items-center gap-1">
                {getEventTypeIcon(event.type)}
                <span className="truncate">{event.title}</span>
              </div>
            </div>
          ))}
          {dayEvents.length > 3 && (
            <div className="text-xs text-muted-foreground">
              +{dayEvents.length - 3} more
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderWeekView = () => {
    return (
      <div className="grid grid-cols-7 gap-4">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="text-sm font-medium text-center p-2 border-b">
            {day}
          </div>
        ))}
        {weekDays.map(date => {
          const dayEvents = getEventsForDate(date)
          const isToday = isSameDay(date, new Date())

          return (
            <div key={date.toISOString()} className="min-h-[300px] p-3 border-r">
              <div className={`
                text-lg font-semibold mb-3
                ${isToday ? 'text-primary' : ''}
              `}>
                {format(date, 'd')}
              </div>
              <div className="space-y-2">
                {dayEvents.map(event => (
                  <div
                    key={event.id}
                    className={`
                      p-2 rounded text-white text-sm
                      ${event.color || 'bg-gray-500'}
                    `}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {getEventTypeIcon(event.type)}
                      <span className="font-medium">{event.title}</span>
                    </div>
                    {event.description && (
                      <p className="text-xs opacity-90">{event.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderListView = () => {
    const sortedEvents = [...filteredEvents].sort((a, b) => 
      new Date(a.date).getTime() - new Date(b.date).getTime()
    )

    return (
      <div className="space-y-4">
        {sortedEvents.map(event => (
          <Card key={event.id}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-4 h-4 rounded-full ${event.color || 'bg-gray-500'}`} />
                  <div>
                    <h3 className="font-medium">{event.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(event.date), 'EEEE, MMMM d, yyyy')}
                    </p>
                    {event.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {event.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {event.type}
                  </Badge>
                  {event.platform && (
                    <Badge variant="secondary">
                      {event.platform}
                    </Badge>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem>Duplicate</DropdownMenuItem>
                      <DropdownMenuItem className="text-destructive">
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Content Calendar</h1>
          <p className="text-muted-foreground">
            Plan and schedule your social media content across all platforms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Schedule Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <PostCreateDialog onClose={() => setIsCreateDialogOpen(false)} />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('prev')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {format(currentDate, 'MMMM yyyy')}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigateMonth('next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              
              <Select value={selectedPlatform} onValueChange={(value) => setSelectedPlatform(value as Platform | 'all')}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="All Platforms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Platforms</SelectItem>
                  <SelectItem value="INSTAGRAM">Instagram</SelectItem>
                  <SelectItem value="FACEBOOK">Facebook</SelectItem>
                  <SelectItem value="TWITTER">Twitter</SelectItem>
                  <SelectItem value="LINKEDIN">LinkedIn</SelectItem>
                </SelectContent>
              </Select>

              <Tabs value={viewMode} onValueChange={(value) => setViewMode(value as 'month' | 'week' | 'list')}>
                <TabsList>
                  <TabsTrigger value="month">Month</TabsTrigger>
                  <TabsTrigger value="week">Week</TabsTrigger>
                  <TabsTrigger value="list">List</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'month' && (
            <div className="border rounded-lg overflow-hidden">
              {/* Calendar Header */}
              <div className="grid grid-cols-7 bg-muted">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="p-3 text-sm font-medium text-center border-r">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map(renderCalendarDay)}
              </div>
            </div>
          )}

          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'list' && renderListView()}
        </CardContent>
      </Card>

      {/* Selected Date Details */}
      {selectedDate && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Events for {format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedDate(null)}
              >
                Close
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getEventsForDate(selectedDate).length === 0 ? (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No events scheduled for this date</p>
                <Button className="mt-4" onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Schedule Post
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {getEventsForDate(selectedDate).map(event => (
                  <Card key={event.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-4 h-4 rounded-full ${event.color || 'bg-gray-500'}`} />
                        <div className="flex-1">
                          <h3 className="font-medium">{event.title}</h3>
                          {event.description && (
                            <p className="text-sm text-muted-foreground">{event.description}</p>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{event.type}</Badge>
                            {event.platform && (
                              <Badge variant="secondary">{event.platform}</Badge>
                            )}
                            {event.status && (
                              <Badge variant={event.status === 'PUBLISHED' ? 'default' : 'secondary'}>
                                {event.status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}